import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, Download, Pause, Play, X, Loader2, RotateCcw } from "lucide-react";

const fmtN = (n) => (n ?? 0).toLocaleString();

function JobPanel({ job, onAction }) {
    if (!job?.running && !job?.finished_at) return null;
    const pct = job.mode === "scan"
        ? (job.last_page ? Math.round((job.page / job.last_page) * 100) : 0)
        : (job.target ? Math.round((job.done / job.target) * 100) : 0);
    return (
        <div className="border border-slate-200 bg-slate-50 p-3 font-mono text-xs space-y-2" data-testid="ee-cat-job-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <span>
                    {job.running ? (job.paused ? <b className="text-amber-600">● PAUZË</b> : <b className="text-emerald-600">● DUKE PUNUAR</b>) : <b className="text-slate-600">● PËRFUNDOI</b>}
                    {" "}{job.mode === "scan" ? "Skanim" : "Import"} · {job.category || job.slug}
                </span>
                {job.running && (
                    <span className="flex gap-1">
                        {job.paused
                            ? <button onClick={() => onAction("resume")} className="btn-primary !py-1 !text-[10px]" data-testid="ee-cat-resume"><Play className="w-3 h-3" /> Vazhdo</button>
                            : <button onClick={() => onAction("pause")} className="btn-outline !py-1 !text-[10px]" data-testid="ee-cat-pause"><Pause className="w-3 h-3" /> Pauzë</button>}
                        <button onClick={() => onAction("cancel")} className="btn-outline !py-1 !text-[10px]" data-testid="ee-cat-cancel"><X className="w-3 h-3" /> Anulo</button>
                    </span>
                )}
            </div>
            <div className="h-1.5 bg-slate-200"><div className="h-full bg-[hsl(var(--primary))] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            {job.mode === "scan"
                ? <div data-testid="ee-cat-job-progress">Faqja {job.page}/{job.last_page} · {fmtN(job.done)} / {fmtN(job.target)} artikuj të lexuar</div>
                : <div data-testid="ee-cat-job-progress">{job.done}/{job.target} · <span className="text-emerald-700">shtuar {job.created}</span> · ekzistonin {job.skipped} · <span className="text-red-600">dështuan {job.failed}</span>{job.current ? ` · ${job.current}` : ""}</div>}
            {job.error && <div className="text-red-600">GABIM: {job.error}</div>}
        </div>
    );
}

export default function EeCategoryImport({ onImported }) {
    const [cats, setCats] = useState([]);
    const [slug, setSlug] = useState("");
    const [queue, setQueue] = useState(null);
    const [job, setJob] = useState(null);
    const [q, setQ] = useState("");
    const [count, setCount] = useState(50);
    const [err, setErr] = useState("");

    const loadCats = useCallback(() => api.get("/admin/ee-turbo/categories").then(({ data }) => setCats(data)).catch((e) => setErr(e.response?.data?.detail || "Kategoritë nuk u ngarkuan")), []);
    const loadQueue = useCallback(() => {
        if (!slug) return setQueue(null);
        api.get(`/admin/ee-turbo/category-queue/${slug}`, { params: { q: q || undefined, limit: 200 } }).then(({ data }) => setQueue(data)).catch(() => {});
    }, [slug, q]);
    const loadJob = useCallback(() => api.get("/admin/ee-turbo/category-job").then(({ data }) => setJob(data)).catch(() => {}), []);

    useEffect(() => { loadCats(); loadJob(); }, [loadCats, loadJob]);
    useEffect(() => { const t = setTimeout(loadQueue, 250); return () => clearTimeout(t); }, [loadQueue]);
    useEffect(() => {
        if (!job?.running) return;
        const id = setInterval(async () => {
            const { data } = await api.get("/admin/ee-turbo/category-job");
            setJob(data);
            if (!data.running) { loadQueue(); loadCats(); onImported?.(); }
        }, 2000);
        return () => clearInterval(id);
    }, [job?.running, loadQueue, loadCats, onImported]);

    const start = async (path, body) => {
        setErr("");
        try {
            await api.post(path, body);
            setJob((j) => ({ ...(j || {}), running: true, paused: false, mode: path.endsWith("scan") ? "scan" : "import", slug, done: 0, page: 0 }));
            setTimeout(loadJob, 1200);
        } catch (e) {
            setErr(e.response?.data?.detail || "Veprimi dështoi");
        }
    };
    const action = (a) => api.post(`/admin/ee-turbo/category-job/${a}`).then(loadJob);
    const running = !!job?.running;
    const filtered = q.trim() ? queue?.filtered_count ?? 0 : queue?.missing_count ?? 0;
    const n = Math.min(count, filtered || 0, 200);

    return (
        <div className="space-y-3" data-testid="ee-category-import">
            <p className="text-xs text-slate-600">
                Zgjidh një kategori, skanoje për të parë çfarë mungon, pastaj importo me grupe (maks 200). Kodet e mbetura ruhen për herë tjetër.
            </p>
            <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[220px]">
                    <label className="label-kicker mb-1 block">Kategoria në ee-turbo</label>
                    <select value={slug} onChange={(e) => { setSlug(e.target.value); setQ(""); }} className="input-field" data-testid="ee-cat-select">
                        <option value="">— Zgjidh kategorinë —</option>
                        {cats.map((c) => (
                            <option key={c.slug} value={c.slug}>
                                {c.name}{c.scanned_at ? ` — ke ${fmtN(c.have)}/${fmtN(c.ee_total)}, mungojnë ${fmtN(c.missing_count)}` : ""}
                            </option>
                        ))}
                    </select>
                </div>
                <button onClick={() => start("/admin/ee-turbo/category-scan", { slug })} disabled={!slug || running} className="btn-outline" data-testid="ee-cat-scan">
                    {running && job?.mode === "scan" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {queue?.scanned_at ? "Riskano" : "Skano"}
                </button>
            </div>

            {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 p-2" data-testid="ee-cat-error">{err}</div>}
            <JobPanel job={job} onAction={action} />

            {queue?.scanned_at && (
                <>
                    <div className="grid grid-cols-3 gap-2" data-testid="ee-cat-summary">
                        {[["Në ee-turbo", queue.ee_total, ""], ["I ke", queue.have, "text-emerald-700"], ["Mungojnë", queue.missing_count, "text-[hsl(var(--primary))]"]].map(([l, v, c]) => (
                            <div key={l} className="border border-slate-200 p-2">
                                <div className="label-kicker text-slate-500">{l}</div>
                                <div className={`font-display font-black text-xl ${c}`} data-testid={`ee-cat-summary-${l.split(" ")[0].toLowerCase()}`}>{fmtN(v)}</div>
                            </div>
                        ))}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500">Skanuar: {new Date(queue.scanned_at).toLocaleString()}</div>

                    <div className="flex flex-wrap gap-2 items-end border-t border-slate-200 pt-3">
                        <div className="flex-1 min-w-[180px] relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtro sipas kodit (p.sh. GT17)" className="input-field !pl-8 !py-1.5 !text-xs" data-testid="ee-cat-filter" />
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">Sa të importoj</label>
                            <input type="number" min="1" max="200" value={count} onChange={(e) => setCount(Math.max(1, Math.min(200, parseInt(e.target.value, 10) || 1)))} className="input-field !py-1.5 !w-24" data-testid="ee-cat-count" />
                        </div>
                        <button
                            onClick={() => start("/admin/ee-turbo/category-import", { slug, count: n, codes: q.trim() ? (queue.missing || []).slice(0, n).map((m) => m.code) : undefined })}
                            disabled={running || n < 1}
                            className="btn-primary"
                            data-testid="ee-cat-import"
                        >
                            <Download className="w-4 h-4" /> Importo {n} {q.trim() ? "të filtruar" : "të radhës"}
                        </button>
                    </div>
                    <div className="text-[11px] text-slate-500">Mbeten pas këtij importi: <b data-testid="ee-cat-remaining">{fmtN(Math.max(0, (queue.missing_count || 0) - n))}</b> kode të ruajtura për më vonë.</div>

                    <div className="border border-slate-200 max-h-72 overflow-y-auto" data-testid="ee-cat-missing-list">
                        {(queue.missing || []).map((m, i) => (
                            <div key={m.id} className={`flex items-center gap-3 px-2 py-1.5 border-b border-slate-100 text-xs ${i < n ? "bg-emerald-50/60" : ""}`} data-testid={`ee-cat-missing-${m.id}`}>
                                <span className="font-mono text-[10px] text-slate-400 w-8 text-right">{i + 1}</span>
                                {m.image ? <img src={m.image} alt="" className="w-9 h-9 object-contain bg-white border border-slate-200" loading="lazy" /> : <div className="w-9 h-9 bg-slate-100" />}
                                <span className="font-bold flex-1">{m.code}</span>
                                <span className="font-mono text-[10px] text-slate-500">stok ee: {m.stock_amount}</span>
                            </div>
                        ))}
                        {(queue.missing || []).length === 0 && <div className="p-4 text-center text-xs text-slate-500">Asgjë nuk mungon.</div>}
                    </div>
                    {filtered > (queue.missing || []).length && <div className="text-[10px] font-mono text-slate-500">Shfaqen 200 të parat nga {fmtN(filtered)}.</div>}

                    {queue.failed?.length > 0 && (
                        <div className="border border-red-200 bg-red-50 p-2 text-xs" data-testid="ee-cat-failed">
                            <div className="flex justify-between items-center mb-1">
                                <b className="text-red-700">Dështuan: {queue.failed.length}</b>
                                <button onClick={() => api.post(`/admin/ee-turbo/category-retry-failed/${slug}`).then(() => { loadQueue(); loadCats(); })} disabled={running} className="btn-outline !py-1 !text-[10px]" data-testid="ee-cat-retry-failed">
                                    <RotateCcw className="w-3 h-3" /> Ktheji në radhë
                                </button>
                            </div>
                            {queue.failed.slice(0, 20).map((f) => <div key={f.id} className="font-mono text-[10px]">{f.code} — {f.error}</div>)}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
